/**
 * Shared types for the seeding panel and its tabs.
 * Kept in a dedicated module so the three tab components and their parent panel
 * depend on a single source of truth rather than cross-importing from siblings.
 */

export interface SeedingTeam {
  readonly id: string;
  readonly name: string;
}

export interface SeedingGroup {
  readonly id: string;
  readonly name: string;
}

export interface SeedingEntrySlot {
  readonly gameId: string;
  readonly gameLabel: string;
  readonly slotIndex: number;
  readonly groupName: string;
  readonly roundName: string;
}

/**
 * Manual-pin state used by both the group-pin and slot-pin tabs.
 *
 * - Only `teamId` is required.
 * - `groupId` set without `gameId` means a pure group pin.
 * - `gameId` + `slotIndex` means a slot pin (may also carry `groupId`).
 */
export interface SeedingPinState {
  readonly teamId: string;
  readonly groupId?: string | null;
  readonly gameId?: string | null;
  readonly slotIndex?: number | null;
}

/**
 * Shape expected by the `updateSeedingPins` mutation.
 * Keeps nulls explicit so the backend can clear previously pinned values.
 */
export interface SeedingPinInput {
  readonly teamId: string;
  readonly groupId: string | null;
  readonly gameId: string | null;
  readonly slotIndex: number | null;
}

/**
 * Normalize a `SeedingPinState` into the input shape required by the API.
 * Defined here (rather than inlined in each tab) so both pin tabs produce the
 * exact same payload and any future shape changes land in one place.
 */
export const toSeedingPinInput = (pin: SeedingPinState): SeedingPinInput => ({
  teamId: pin.teamId,
  groupId: pin.groupId ?? null,
  gameId: pin.gameId ?? null,
  slotIndex: pin.slotIndex ?? null,
});
